import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { NotePencil, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { jstDateTime } from '@/utils/jstDate';
import { devApi } from './devApi';

/**
 * 引継ぎメモ（次の担当へ）— 2026-10-23。
 * 自動で拾える項目（未解決の課題・保守の注意・予備部品）では書けないことを、
 * 担当が自分の言葉で残すための欄。完了報告書の「引継ぎ事項」の先頭に出る。
 */
export const HandoverMemoCard = ({ devId, memo = '', updatedByName = null,
  updatedAt = null, history = [], canEdit = false, onChanged }) => {
  const [text, setText] = useState(memo || '');
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  useEffect(() => { setText(memo || ''); }, [memo]);

  const save = async () => {
    setBusy(true);
    try {
      await devApi.patch(devId, { handover_memo: text });
      toast.success('引継ぎメモを保存しました');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-lg border border-zinc-300 bg-white shadow-sm"
      data-testid="dev-handover-memo-card">
      <div className="flex items-center justify-between border-b border-zinc-300 bg-zinc-100 px-4 py-2">
        <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
          <NotePencil size={14} /> 引継ぎメモ（次の担当へ）
        </p>
        {history.length > 0 && (
          <button type="button" onClick={() => setShowHistory((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900"
            data-testid="dev-handover-memo-history-toggle">
            <ClockCounterClockwise size={12} /> 変更履歴（{history.length}）
          </button>
        )}
      </div>
      <div className="px-4 py-3">
        <p className="text-[11px] text-zinc-500">
          完了報告書の「引継ぎ事項」の先頭に出ます。引き渡し時の内容がそのまま控えに残ります。
        </p>
        {canEdit ? (
          <>
            <Textarea value={text} rows={4} className="mt-2 text-sm"
              onChange={(e) => setText(e.target.value)}
              placeholder="例: 立ち上げ時はヒーターの昇温を10分待つこと。予備のばねは技術部の棚にあります。"
              data-testid="dev-handover-memo-input" />
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" onClick={save} disabled={busy || text === (memo || '')}
                data-testid="dev-handover-memo-save">保存</Button>
              {updatedAt && (
                <span className="text-[11px] text-zinc-500" data-testid="dev-handover-memo-stamp">
                  最終更新 {jstDateTime(updatedAt)}{updatedByName ? ` · ${updatedByName}` : ''}
                </span>
              )}
            </div>
          </>
        ) : (
          <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-900"
            data-testid="dev-handover-memo-text">
            {memo || 'まだ書かれていません。'}
          </p>
        )}
        {showHistory && (
          <ul className="mt-2 space-y-1 border-t border-zinc-200 pt-2"
            data-testid="dev-handover-memo-history">
            {history.map((h) => (
              <li key={h.id} className="text-[11px] text-zinc-600">
                {jstDateTime(h.changed_at)} · {h.changed_by_name}
                <span className="block whitespace-pre-wrap text-zinc-800">{h.after || '（空に戻した）'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default HandoverMemoCard;
